import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTIES, MARKET_TABS, STRATEGIES } from '@/lib/buyersV2';

const cls = 'bg-white border-gray-300 h-9 text-sm';
const FIELDS = [
  ['entity_name', 'Entity name'], ['sunbiz_doc_number', 'Sunbiz Doc #'], ['entity_status', 'Entity status'], ['ownership_chain', 'Ownership chain'],
  ['sunbiz_mailing_address', 'Sunbiz mailing address'], ['county_mailing_address', 'County mailing address'], ['status', 'Status'],
];

function Sel({ label, value, options, onChange, testid }) {
  return (
    <div className="space-y-1"><Label className="text-[11px] text-gray-500">{label}</Label>
      <Select value={value || '__none__'} onValueChange={v => onChange(v === '__none__' ? '' : v)}>
        <SelectTrigger data-testid={testid} className={cls}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="__none__">—</SelectItem>{options.map(o => <SelectItem key={o} value={String(o)}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function ProfileCard({ buyer, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF({ ...buyer, preferred_zips_text: (buyer.preferred_zips || []).join(', '), strategy: buyer.strategy_imported || '', buyer_score: buyer.buyer_score_imported ? String(buyer.buyer_score_imported) : '' });
  }, [buyer]);
  const up = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || ''])), county: f.county || '', market_tab: f.market_tab || '', notes: f.notes || '',
        zip_preference: !!f.zip_preference, preferred_zips: (f.preferred_zips_text || '').split(/[,\s]+/).filter(Boolean), strategy: f.strategy || '', buyer_score: f.buyer_score || 0 };
      await API.put(`/buyers/${buyer.id}/profile`, body); toast.success('Profile saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="profile-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><Building2 className="h-4 w-4 text-[#0073ea]" /> Entity Profile</p>
          <Button size="sm" data-testid="save-profile-btn" onClick={save} disabled={saving} className="bg-[#0073ea] hover:bg-[#0060c2] text-white"><Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1"><Label className="text-[11px] text-gray-500">{l}</Label><Input data-testid={`profile-${k}`} value={f[k] || ''} onChange={e => up(k, e.target.value)} className={cls} /></div>
          ))}
          <Sel label="County" value={f.county} options={COUNTIES} onChange={v => up('county', v)} testid="profile-county" />
          <Sel label="Market tab" value={f.market_tab} options={MARKET_TABS} onChange={v => up('market_tab', v)} testid="profile-market" />
          <Sel label="Strategy (override)" value={f.strategy} options={STRATEGIES} onChange={v => up('strategy', v)} testid="profile-strategy" />
          <Sel label="Buyer score (override)" value={f.buyer_score} options={[1, 2, 3, 4, 5]} onChange={v => up('buyer_score', v)} testid="profile-score" />
          <div className="space-y-1"><Label className="text-[11px] text-gray-500">Preferred zips</Label><Input data-testid="profile-zips" value={f.preferred_zips_text || ''} onChange={e => up('preferred_zips_text', e.target.value)} className={cls} placeholder="33157, 33176" /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700 pt-5"><Switch data-testid="profile-zip-pref" checked={!!f.zip_preference} onCheckedChange={v => up('zip_preference', v)} /> Zip preference</label>
        </div>
        <div className="space-y-1"><Label className="text-[11px] text-gray-500">Notes</Label><Textarea data-testid="profile-notes" value={f.notes || ''} onChange={e => up('notes', e.target.value)} rows={2} className="bg-white border-gray-300 text-sm" /></div>
        <p className="text-[11px] text-gray-400">Sources: {(buyer.sources || []).join(', ') || '—'}</p>
      </CardContent>
    </Card>
  );
}
